<template>
  <div
    class="asc-fleet-health-score-wrapper"
    data-automation-id="wrapper_fleet_health_score"
  >
    <template v-if="loading">
      <asc-spinner-wrapper loading></asc-spinner-wrapper>
    </template>

    <template v-if="!loading">
      <div class="asc-fleet-health-score-wrapper--content">
        <div class="asc-fleet-health-score-wrapper--filter-options">
          <div
            :class="[
              isFeatureInvestigator
                ? 'asc-fleet-health-score-wrapper--filter-content'
                : 'asc-fleet-health-score-wrapper--filter-content-full-width',
            ]"
          >
            <asc-chips-filter-manager
              :default-filter-options="healthScoreDefaultFilterOptions"
              @filterChanged="handleHealthScoreFiltersChange"
            ></asc-chips-filter-manager>
          </div>

          <template v-if="isFeatureInvestigator">
            <div class="asc-fleet-health-score-wrapper--feature-toggle">
              <asc-switch
                v-model="isSwitchChecked"
                label="Include Internal Scores"
                hide-details
                single-line
                @input="handleToggleIncludeInternalScores"
              ></asc-switch>
            </div>
          </template>
        </div>

        <asc-filter-legend
          :items="getLegendItems"
          :items-selected="legendItemsSelected"
          :on-item-click="handleLegendItemClick"
          tooltip="Select any status to filter below"
        ></asc-filter-legend>

        <div class="asc-fleet-health-score-wrapper--chart-content">
          <div class="asc-fleet-health-score-wrapper--bar-chart-left">
            <asc-health-score-bar-chart
              :title="getTitleByFeature"
              :values="filteredHealthScoreByTailData"
              :loading="loading"
              :tooltip="getTooltipByFeature"
              :selected-item="getSelectedTail"
              type="healthTail"
              @onSeriesClick="handleTailSelected"
            ></asc-health-score-bar-chart>
          </div>
          <div class="asc-fleet-health-score-wrapper--bar-chart-right">
            <asc-health-score-bar-chart
              :title="getTitleForRiskScores"
              :values="filteredHealthScoreByFeatureData"
              :loading="loading"
              :tooltip="getTooltipForRiskScores"
              :selected-item="selectedFeature"
              :show-button="false"
              type="healthFeature"
              @onSeriesClick="handleFeatureSelected"
            ></asc-health-score-bar-chart>
          </div>
        </div>

        <div class="asc-fleet-health-score-wrapper--plot-content">
          <asc-header
            class="asc-fleet-health-score-wrapper--plot-title"
            uppercase
            size="h4"
            :value="getPlotTitle"
          />
          <asc-spinner-wrapper :loading="loadingPlot">
            <asc-health-score-plot-wrapper
              :plot-data="healthScoreTrendData"
              :phm-alert-data="healthScorePhmAlertData"
              :mx-rec-overlays-data="healthScoreMxRecOverlaysData"
              :selected-item="selectedTail"
              :selected-feature="selectedFeature"
            ></asc-health-score-plot-wrapper>
          </asc-spinner-wrapper>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import AscHeader from '@/components/atoms/AscHeader'
import AscSpinnerWrapper from '@/components/atoms/AscSpinnerWrapper'
import AscSwitch from '@/components/atoms/AscSwitch'
import AscChipsFilterManager from '@/components/molecules/AscChipsFilterManager'
import AscFilterLegend from '@/components/molecules/AscFilterLegend'
import AscHealthScoreBarChart from '@/components/molecules/AscHealthScoreBarChart'
import variables from '@/styles/_varsJs.scss'
import AscHealthScorePlotWrapper from './AscHealthScorePlotWrapper'

export default {
  name: 'AscFleetHealthScoreWrapper',

  components: {
    AscChipsFilterManager,
    AscFilterLegend,
    AscHeader,
    AscHealthScoreBarChart,
    AscHealthScorePlotWrapper,
    AscSpinnerWrapper,
    AscSwitch,
  },

  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    includeInternalScores: {
      type: Boolean,
      default: false,
    },
    isFeatureInvestigator: {
      type: Boolean,
      default: false,
    },
    healthScoreDefaultFilterOptions: {
      type: Array,
      default: () => [],
    },
    healthScoreByTailData: {
      type: Array,
      default: () => [],
    },
    healthScoreByFeatureData: {
      type: Array,
      default: () => [],
    },
    healthScoreTrendData: {
      type: Object,
      default: () => {},
    },
    healthScorePhmAlertData: {
      type: Array,
      default: () => [],
    },
    healthScoreMxRecOverlaysData: {
      type: Array,
      default: () => [],
    },
    loadingPlot: {
      type: Boolean,
      default: false,
    },
    onHealthScoreFiltersChange: {
      type: Function,
      default: () => {},
    },
    onHealthScoreTailChange: {
      type: Function,
      default: () => {},
    },
    onHealthScoreFeatureChange: {
      type: Function,
      default: () => {},
    },
    onToggleIncludeInternalScores: {
      type: Function,
      default: () => {},
    },
  },

  data() {
    return {
      isSwitchChecked: this.includeInternalScores,
      selectedTail: '',
      selectedFeature: '',
      filteredTailData: [],
      filteredFeatureData: [],
      legendItemsSelected: this.getLegendItemsSelectedFromLocalStorage(),
    }
  },

  computed: {
    getTitleByFeature() {
      return 'Health Score Comparison by Tail'
    },

    getTooltipByFeature() {
      return 'Health score data represents the latest flight for each tail. Visualization defaults to show the top 15 tails ranked by urgency as defined by cumulative health score.'
    },

    getTitleForRiskScores() {
      return `Health Score Comparison for ${this.selectedTail}`
    },

    getTooltipForRiskScores() {
      return 'Health score data representing the latest flight for each tail.'
    },

    getPlotTitle() {
      return `Tail ${this.selectedTail} ${this.selectedFeature} Trend`
    },

    getSelectedTail() {
      return this.selectedTail
    },

    getLegendItems() {
      return [
        {
          header: '',
          items: [
            {
              text: 'Health Score at or Above 1.0',
              styleColor: variables.warningbase,
            },
            {
              text: 'Health Score Below 1.0',
              styleColor: variables.accent500,
            },
          ],
        },
      ]
    },

    filteredHealthScoreByTailData() {
      const filteredData = this.healthScoreByTailData.map(item => {
        return {
          name: item.name,
          highScoreSummation: this.isHealthScoreTypeSelected(
            'Health Score at or Above 1.0',
          )
            ? 0
            : item.highScoreSummation,
          lowScoreSummation: this.isHealthScoreTypeSelected(
            'Health Score Below 1.0',
          )
            ? 0
            : item.lowScoreSummation,
        }
      })

      const highScoreSelected = this.isHealthScoreTypeSelected(
        'Health Score at or Above 1.0',
      )

      if (highScoreSelected) {
        return filteredData.sort(
          (a, b) =>
            b.lowScoreSummation - a.lowScoreSummation ||
            a.name.localeCompare(b.name),
        )
      }

      return filteredData
    },

    filteredHealthScoreByFeatureData() {
      return this.healthScoreByFeatureData.filter(item => {
        const highScoreSelected = this.isHealthScoreTypeSelected(
          'Health Score at or Above 1.0',
        )
        const lowScoreSelected = this.isHealthScoreTypeSelected(
          'Health Score Below 1.0',
        )

        if (highScoreSelected && item.riskScore >= 1) {
          return false
        }

        if (lowScoreSelected && item.riskScore < 1) {
          return false
        }

        return true
      })
    },
  },

  watch: {
    healthScoreByTailData(newVal) {
      this.healthScoreByTailData = newVal
      this.initTailNumber()
    },
    healthScoreByFeatureData(newVal) {
      this.healthScoreByFeatureData = newVal
      this.initFeature()
    },
  },

  mounted() {
    // load selectedTail to the first item if selectedTail is empty
    if (this.selectedTail === '') {
      const filteredData1 = this.filteredHealthScoreByTailData
      const [firstTail] = filteredData1
      if (firstTail) {
        this.selectedTail = firstTail.name
      }
    }

    // load selectedFeature to the first item if selectedFeature is empty
    if (this.selectedFeature === '') {
      const filteredData2 = this.filteredHealthScoreByFeatureData
      const [firstFeature] = filteredData2
      if (firstFeature) {
        this.selectedFeature = firstFeature.name
      }
    }
  },

  methods: {
    handleToggleIncludeInternalScores() {
      if (this.onToggleIncludeInternalScores) {
        this.onToggleIncludeInternalScores(this.isSwitchChecked)
      }
    },

    isHealthScoreTypeSelected(healthScoreType) {
      if (!this.legendItemsSelected) {
        return false
      }

      return this.legendItemsSelected.includes(healthScoreType)
    },

    getLegendItemsSelectedFromLocalStorage() {
      const items = localStorage.getItem('fleet_health_index_legend_items')

      return items ? JSON.parse(items) : []
    },

    setLegendItemsSelectedToLocalStorage() {
      localStorage.setItem(
        'fleet_health_index_legend_items',
        JSON.stringify(this.legendItemsSelected),
      )
    },

    handleLegendItemClick(status) {
      this.selectedTail = null
      const isFound = this.legendItemsSelected.includes(status)
      if (isFound) {
        this.legendItemsSelected = this.legendItemsSelected.filter(
          item => item !== status,
        )
      } else {
        this.legendItemsSelected.push(status)
      }

      this.initTailNumber()

      this.setLegendItemsSelectedToLocalStorage()
    },

    handleHealthScoreFiltersChange(filters) {
      if (this.onHealthScoreFiltersChange) {
        this.onHealthScoreFiltersChange(filters)
      }
    },

    handleTailSelected(tailNumber) {
      this.selectedTail = tailNumber
      if (this.onHealthScoreTailChange) {
        this.onHealthScoreTailChange(tailNumber)
      }
    },

    handleFeatureSelected(feature) {
      this.selectedFeature = feature
      if (this.onHealthScoreFeatureChange) {
        this.onHealthScoreFeatureChange(this.selectedTail, feature)
      }
    },

    initTailNumber() {
      const filterdData = this.filteredHealthScoreByTailData
      const [first] = filterdData
      if (first) {
        this.handleTailSelected(first.name)
      }
    },

    initFeature() {
      const filteredData = this.filteredHealthScoreByFeatureData
      const [first] = filteredData
      if (first) {
        this.handleFeatureSelected(first.name)
      }
    },
  },
}
</script>

<style lang="scss">
.asc-fleet-health-score-wrapper {
  padding: 16px;
  background-color: $primary-white;

  .asc-fleet-health-score-wrapper--filter-options {
    display: flex;
    max-width: 100%;

    .asc-fleet-health-score-wrapper--filter-content-full-width {
      flex: 1;
      max-width: calc(100% - 8px);
      margin-bottom: 16px;
    }

    .asc-fleet-health-score-wrapper--filter-content {
      flex: 1;
      max-width: calc(100% - 216px);
      margin-bottom: 16px;
      margin-right: 16px;
    }

    .asc-fleet-health-score-wrapper--feature-toggle {
      width: 200px;
      padding-top: 5px;
    }
  }

  &--chart-content {
    display: flex;
    margin: 16px 0;
    width: 100%;

    .asc-fleet-health-score-wrapper--bar-chart-left {
      width: 50%;
      margin: 0 8px 0 0;
      padding: 8px;
      border: 1px solid $gray-300;
    }

    .asc-fleet-health-score-wrapper--bar-chart-right {
      width: 50%;
      margin: 0 0 0 8px;
      padding: 8px;
      border: 1px solid $gray-300;
    }
  }

  &--plot-content {
    margin: 16px 0;
    padding: 16px;
    border: 1px solid $gray-300;

    .asc-fleet-health-score-wrapper--plot-title {
      & .asc-header--h4 {
        font-size: 16px !important;
        font-weight: 500 !important;
        line-height: 18px !important;
        letter-spacing: 1px !important;
      }
    }
  }
}
</style>
